import React from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  width,
  height,
  animation = 'pulse',
}) => {
  const baseClass = 'bg-gray-200';
  
  const variantClass = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  }[variant];

  const animationClass = {
    pulse: 'animate-pulse',
    wave: 'animate-shimmer',
    none: '',
  }[animation];

  const style = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height || (variant === 'text' ? '1em' : undefined),
  };

  return (
    <div
      className={`${baseClass} ${variantClass} ${animationClass} ${className}`}
      style={style}
    />
  );
};

// Skeleton layouts for different page types
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({ rows = 5, columns = 5 }) => (
  <div className="space-y-3">
    {/* Header */}
    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} height={20} />
      ))}
    </div>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton key={colIndex} height={16} />
        ))}
      </div>
    ))}
  </div>
);

export const CardSkeleton: React.FC = () => (
  <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-4">
    <div className="flex items-center gap-4">
      <Skeleton variant="circular" width={48} height={48} />
      <div className="flex-1 space-y-2">
        <Skeleton width="60%" height={20} />
        <Skeleton width="40%" height={16} />
      </div>
    </div>
    <div className="space-y-2">
      <Skeleton width="100%" height={16} />
      <Skeleton width="90%" height={16} />
      <Skeleton width="80%" height={16} />
    </div>
  </div>
);

export const StatCardSkeleton: React.FC = () => (
  <div className="rounded-xl border border-gray-200 bg-white p-6">
    <Skeleton width="40%" height={14} className="mb-2" />
    <Skeleton width="60%" height={32} />
  </div>
);

export const DetailPageSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Profile Card */}
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <Skeleton width={100} height={24} className="mb-6" />
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <Skeleton variant="circular" width={64} height={64} />
          <div className="space-y-2">
            <Skeleton width={200} height={24} />
            <Skeleton width={300} height={16} />
          </div>
        </div>
        <div className="flex gap-3">
          <Skeleton width={100} height={32} />
          <Skeleton width={150} height={32} />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-6 border-t border-gray-200 pt-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton width="80%" height={12} />
            <Skeleton width="60%" height={20} />
          </div>
        ))}
      </div>
    </div>

    {/* Tabs */}
    <div className="flex gap-8 border-b border-gray-200">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} width={100} height={20} className="mb-3" />
      ))}
    </div>

    {/* Content Cards */}
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  </div>
);
